(() => {
  'use strict';

  if (window.PeptibluStorefrontLoaded) return;
  window.PeptibluStorefrontLoaded = true;

  const nativeFetch = window.fetch.bind(window);
  const giftConfig = window.PeptibluGift || {};
  const giftVariantId = Number(giftConfig.giftVariantId || 0);
  const qualifyingVariantId = Number(giftConfig.qualifyingVariantId || 0);
  let giftSyncPromise = null;
  let giftSyncTimer = null;
  let giftMonitor = null;

  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path.replace(/^\//, '')}`;
  const moneyFromCard = (card, name) => card.dataset[name] || '';
  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input?.url || '';
  };
  const isCartMutation = (input) => /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(requestUrl(input));

  const updateCartCount = (cart) => {
    const cartCount = document.querySelector('.pb-cart-link span');
    const cartLink = document.querySelector('.pb-cart-link');
    if (!cart || !cartCount || !cartLink) return;
    cartCount.textContent = String(cart.item_count);
    cartLink.setAttribute('aria-label', `Shopping bag, ${cart.item_count} items`);
  };

  const fetchCart = async () => {
    const response = await nativeFetch(route('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  };

  const changeLine = async (key, quantity) => {
    const response = await nativeFetch(route('cart/change.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error('Unable to update gift');
    return response.json();
  };

  const syncMysteryGift = async () => {
    if (!giftVariantId || !qualifyingVariantId) return { enabled: false, changed: false };
    if (giftSyncPromise) return giftSyncPromise;

    giftSyncPromise = (async () => {
      const cart = await fetchCart();
      const eligibleQuantity = cart.items
        .filter((item) => Number(item.variant_id) === qualifyingVariantId)
        .reduce((sum, item) => sum + item.quantity, 0);
      const giftLines = cart.items.filter((item) => Number(item.variant_id) === giftVariantId);
      let changed = false;
      let unavailable = false;

      if (eligibleQuantity > 0 && giftLines.length === 0) {
        const response = await nativeFetch(route('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: giftVariantId,
              quantity: 1,
              properties: {
                _peptiblu_mystery_gift: 'Full Reset gift',
                'Gift offer': '$15 value · while supplies last',
              },
            }],
          }),
        });
        if (response.ok) changed = true;
        else unavailable = true;
      } else if (eligibleQuantity === 0 && giftLines.length > 0) {
        for (const line of giftLines) await changeLine(line.key, 0);
        changed = true;
      } else if (eligibleQuantity > 0 && giftLines.length > 0) {
        if (giftLines[0].quantity !== 1) {
          await changeLine(giftLines[0].key, 1);
          changed = true;
        }
        for (const extraLine of giftLines.slice(1)) {
          await changeLine(extraLine.key, 0);
          changed = true;
        }
      }

      const finalCart = changed ? await fetchCart() : cart;
      const result = {
        enabled: true,
        eligible: eligibleQuantity > 0,
        giftPresent: finalCart.items.some((item) => Number(item.variant_id) === giftVariantId),
        unavailable,
        changed,
        cart: finalCart,
      };
      if (result.giftPresent && !giftMonitor) giftMonitor = window.setInterval(() => syncMysteryGift(), 5000);
      if (!result.giftPresent && giftMonitor) {
        window.clearInterval(giftMonitor);
        giftMonitor = null;
      }
      updateCartCount(finalCart);
      document.dispatchEvent(new CustomEvent('peptiblu:gift-synced', { detail: result }));
      return result;
    })().catch(() => ({ enabled: true, changed: false, error: true })).finally(() => {
      giftSyncPromise = null;
    });

    return giftSyncPromise;
  };

  const queueGiftSync = () => {
    window.clearTimeout(giftSyncTimer);
    giftSyncTimer = window.setTimeout(() => syncMysteryGift(), 120);
  };

  const installCartObserver = () => {
    if (!giftVariantId || !qualifyingVariantId || window.fetch?.peptibluGiftObserver) return;
    const observedFetch = window.fetch.bind(window);
    const giftAwareFetch = async (...args) => {
      const response = await observedFetch(...args);
      if (response.ok && isCartMutation(args[0])) {
        response.clone().json().then((cart) => {
          if (Number.isFinite(Number(cart?.item_count))) updateCartCount(cart);
        }).catch(() => {});
        queueGiftSync();
      }
      return response;
    };
    giftAwareFetch.peptibluGiftObserver = true;
    window.fetch = giftAwareFetch;
  };

  if (giftVariantId && qualifyingVariantId) {
    installCartObserver();
    window.addEventListener('load', installCartObserver, { once: true });
    window.PeptibluSyncGift = syncMysteryGift;
  }

  const initProduct = (root) => {
    const form = root.querySelector('[data-pb-product-form]');
    if (!form) return;
    const cards = [...root.querySelectorAll('[data-pb-variant-card]')];
    const gift = root.querySelector('[data-pb-full-reset]');
    const add = root.querySelector('[data-pb-add]');
    const addLabel = root.querySelector('[data-pb-add-label]');
    const addPrice = root.querySelector('[data-pb-add-price]');
    const stock = root.querySelector('[data-pb-stock]');
    const selectedLabel = root.querySelector('[data-pb-selected-label]');
    const notice = root.querySelector('[data-pb-form-message]');
    const oneTime = root.querySelector('[data-pb-one-time]');
    const subscribe = root.querySelector('[data-pb-subscribe]');
    const sellingPlan = root.querySelector('[data-pb-selling-plan]');
    const oneTimePrice = root.querySelector('[data-pb-one-time-price]');
    const planPrice = root.querySelector('[data-pb-plan-price]');
    const planName = root.querySelector('[data-pb-plan-name]');
    const cartChoice = root.querySelector('[data-pb-cart-choice]');
    const cartChoiceSummary = cartChoice?.querySelector('[data-pb-cart-choice-summary]');
    const cartChoiceGift = cartChoice?.querySelector('[data-pb-cart-choice-gift]');
    const cartChoiceCheckout = cartChoice?.querySelector('[data-pb-cart-choice-checkout]');
    const cartChoiceTotal = cartChoice?.querySelector('[data-pb-cart-choice-total]');
    let active = cards.find((card) => card.querySelector('input:checked')) || cards[0];

    const closeCartChoice = () => {
      if (!cartChoice || cartChoice.hidden) return;
      cartChoice.classList.remove('is-open');
      document.body.classList.remove('pb-cart-choice-open');
      window.setTimeout(() => { cartChoice.hidden = true; }, 220);
      add?.focus();
    };

    const showCartChoice = ({ title, price, quantity, giftResult }) => {
      if (!cartChoice) return;
      const cartTotal = Number(giftResult?.cart?.total_price);
      const cartCurrency = giftResult?.cart?.currency || 'USD';
      const formattedTotal = Number.isFinite(cartTotal)
        ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: cartCurrency }).format(cartTotal / 100)
        : price;
      if (cartChoiceSummary) cartChoiceSummary.textContent = `${title} (${quantity} ${quantity === 1 ? 'serum' : 'serums'}) has been added to your bag.`;
      if (cartChoiceTotal) cartChoiceTotal.textContent = `· ${formattedTotal}`;
      if (cartChoiceGift) cartChoiceGift.hidden = !(quantity === 3 && giftResult?.giftPresent);
      cartChoice.hidden = false;
      document.body.appendChild(cartChoice);
      document.body.classList.add('pb-cart-choice-open');
      window.requestAnimationFrame(() => {
        cartChoice.classList.add('is-open');
        cartChoiceCheckout?.focus();
      });
    };

    cartChoice?.querySelectorAll('[data-pb-cart-choice-close]').forEach((button) => button.addEventListener('click', closeCartChoice));
    cartChoice?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeCartChoice();
    });

    const setGallery = (index) => {
      const images = [...root.querySelectorAll('[data-pb-gallery-image]')];
      const thumbs = [...root.querySelectorAll('[data-pb-gallery-thumb]')];
      if (!images.length) return;
      const safeIndex = (index + images.length) % images.length;
      images.forEach((image, i) => image.hidden = i !== safeIndex);
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === safeIndex)));
    };

    const updatePlanPrice = () => {
      if (!sellingPlan || !subscribe) return;
      const selectedPlan = sellingPlan.options[sellingPlan.selectedIndex];
      const cardPlanPrice = active?.dataset?.planPrice;
      const cardPlanName = active?.dataset?.planName;
      const effectivePlanPrice = selectedPlan?.dataset?.price || cardPlanPrice || moneyFromCard(active, 'price');
      if (planPrice) planPrice.textContent = effectivePlanPrice;
      if (planName) planName.textContent = selectedPlan?.textContent || cardPlanName || '';
      if (addPrice && subscribe.checked) addPrice.innerHTML = `${effectivePlanPrice} <span aria-hidden="true">→</span>`;
    };

    const changeActive = (card) => {
      if (!card || card.dataset.available !== 'true') return;
      active = card;
      cards.forEach((item) => item.classList.toggle('is-selected', item === active));
      const input = active.querySelector('input');
      input.checked = true;
      const quantity = Number(active.dataset.quantity || 1);
      const price = moneyFromCard(active, 'price');
      const compare = moneyFromCard(active, 'compare');
      if (selectedLabel) selectedLabel.textContent = quantity === 2 ? 'Most-loved set selected' : `${quantity}-serum set selected`;
      if (addLabel) addLabel.textContent = `Order now — ${quantity === 1 ? '1 serum' : `${quantity} serums`}`;
      if (addPrice) addPrice.innerHTML = `${price} <span aria-hidden="true">→</span>`;
      if (oneTimePrice) oneTimePrice.textContent = price;
      if (stock) stock.innerHTML = '<i></i>Order now';
      if (add) add.disabled = false;
      if (gift) gift.classList.toggle('is-active', quantity === 3);
      const mediaId = active.dataset.mediaId;
      const mediaImage = mediaId && root.querySelector(`[data-pb-gallery-image][data-media-id="${mediaId}"]`);
      if (mediaImage) setGallery([...root.querySelectorAll('[data-pb-gallery-image]')].indexOf(mediaImage));
      if (sellingPlan) {
        const template = root.querySelector(`[data-pb-plans-template="${active.dataset.variantId}"]`);
        if (template) sellingPlan.replaceChildren(template.content.cloneNode(true));
      }
      if (subscribe && subscribe.checked) updatePlanPrice();
      if (notice) { notice.textContent = ''; notice.classList.remove('is-error'); }
      if (compare) active.setAttribute('data-has-compare', 'true');
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => changeActive(card));
      card.querySelector('input')?.addEventListener('change', () => changeActive(card));
    });
    gift?.addEventListener('click', () => changeActive(cards.find((card) => card.dataset.title === 'The Full Reset')));
    root.querySelector('[data-pb-gallery-prev]')?.addEventListener('click', () => {
      const images = [...root.querySelectorAll('[data-pb-gallery-image]')];
      setGallery(images.findIndex((image) => !image.hidden) - 1);
    });
    root.querySelector('[data-pb-gallery-next]')?.addEventListener('click', () => {
      const images = [...root.querySelectorAll('[data-pb-gallery-image]')];
      setGallery(images.findIndex((image) => !image.hidden) + 1);
    });
    root.querySelectorAll('[data-pb-gallery-thumb]').forEach((button, index) => button.addEventListener('click', () => setGallery(index)));
    oneTime?.addEventListener('change', () => {
      if (oneTime.checked && sellingPlan) sellingPlan.disabled = true;
      if (addPrice) addPrice.innerHTML = `${moneyFromCard(active, 'price')} <span aria-hidden="true">→</span>`;
    });
    subscribe?.addEventListener('change', () => {
      if (subscribe.checked && sellingPlan) { sellingPlan.disabled = false; updatePlanPrice(); }
    });
    sellingPlan?.addEventListener('change', updatePlanPrice);

    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !window.Shopify?.routes?.root) return;
      event.preventDefault();
      if (!add || add.disabled) return;
      const original = addLabel?.textContent;
      const selectedQuantity = Number(active.dataset.quantity || 1);
      const selectedTitle = active.dataset.title || `${selectedQuantity}-serum set`;
      const selectedPrice = moneyFromCard(active, 'price');
      add.disabled = true;
      if (addLabel) addLabel.textContent = 'Adding to bag…';
      try {
        const response = await nativeFetch(route('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Unable to add this item');
        const item = await response.json();
        const giftResult = await syncMysteryGift();
        if (notice) {
          if (selectedQuantity === 3 && giftResult.giftPresent) notice.textContent = 'Your order and FREE Mystery Gift have been added.';
          else if (selectedQuantity === 3 && giftResult.unavailable) notice.textContent = 'Your order was added. The mystery gift is currently unavailable.';
          else notice.textContent = 'Your order has been added.';
          notice.classList.remove('is-error');
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        updateCartCount(giftResult?.cart);
        showCartChoice({ title: selectedTitle, price: selectedPrice, quantity: selectedQuantity, giftResult });
      } catch (error) {
        if (notice) { notice.textContent = 'We could not add this item. Please try again.'; notice.classList.add('is-error'); }
      } finally {
        add.disabled = false;
        if (addLabel) addLabel.textContent = original || 'Add to bag';
      }
    });
    changeActive(active);
  };

  const initHeader = () => {
    const toggle = document.querySelector('[data-pb-menu-toggle]');
    const menu = document.querySelector('[data-pb-mobile-nav]');
    toggle?.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      menu?.classList.toggle('is-open', !expanded);
    });
  };

  const initReviewSummary = () => {
    document.querySelectorAll('[data-pb-live-rating]').forEach((summary) => {
      const productId = String(summary.dataset.productId || '');
      const ratingValue = summary.querySelector('[data-pb-rating-value]');
      const reviewCount = summary.querySelector('[data-pb-rating-count]');
      let observer;

      const render = () => {
        const previewBadge = summary.querySelector('.jdgm-prev-badge[data-average-rating]');
        const widgetData = window.jdgm?.data?.reviewWidget?.[productId];
        const legacyWidget = document.querySelector('#judgeme_product_reviews .jdgm-rev-widg[data-average-rating]');
        const average = Number(previewBadge?.dataset.averageRating || widgetData?.average_rating || legacyWidget?.dataset.averageRating);
        const count = Number(previewBadge?.dataset.numberOfReviews || widgetData?.number_of_reviews || legacyWidget?.dataset.numberOfReviews);
        if (!Number.isFinite(average) || !Number.isFinite(count)) return false;

        if (ratingValue) ratingValue.textContent = average.toFixed(1);
        if (reviewCount) reviewCount.textContent = `Read ${count} review${count === 1 ? '' : 's'}`;
        summary.setAttribute('aria-label', `Rated ${average.toFixed(1)} out of 5 from ${count} review${count === 1 ? '' : 's'}. Read reviews.`);
        summary.classList.add('is-ready');
        observer?.disconnect();
        return true;
      };

      if (render()) return;
      observer = new MutationObserver(render);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating', 'data-number-of-reviews'] });
      window.setTimeout(() => observer?.disconnect(), 12000);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-peptiblu-product]').forEach(initProduct);
    initHeader();
    initReviewSummary();
    installCartObserver();
    document.addEventListener('click', (event) => {
      if (event.target.closest('cart-remove-button, .quantity__button, [data-cart-remove], [data-cart-item-remove]')) {
        window.setTimeout(queueGiftSync, 450);
      }
    }, true);
    document.addEventListener('change', (event) => {
      if (event.target.closest('quantity-input, .quantity__input, [name="updates[]"]')) {
        window.setTimeout(queueGiftSync, 450);
      }
    }, true);
    syncMysteryGift();
  });
})();
